import { PlayerPosition, GAME_WIDTH, GAME_HEIGHT } from '@hokm/shared';

/**
 * Centralized position configuration for all player-related UI elements.
 * All positions are for DISPLAY positions (local player always at SOUTH).
 */

// Core layout constants
const CENTER_X = GAME_WIDTH / 2;
const CENTER_Y = GAME_HEIGHT / 2 - 30; // Game center (where trick cards meet)
const HAND_Y = GAME_HEIGHT - 80;

// Margins from edges
const SIDE_MARGIN = 50;
const LABEL_SIDE_MARGIN = 150;

export interface PlayerPositionConfig {
  // Player name/score label position
  label: { x: number; y: number };
  // Opponent card fan position (card backs)
  cards: { x: number; y: number; rotation: number; spacing: number; direction: 'h' | 'v' };
  // Where card animations start from (flying to trick center)
  animationStart: { x: number; y: number };
  // Where cards fly to when collected (off-screen)
  offScreen: { x: number; y: number };
  // Hakem card reveal target
  hakemReveal: { x: number; y: number; rotation: number };
  // Turn token offset from label position
  turnToken: { x: number; y: number };
}

export const PLAYER_POSITIONS: Record<PlayerPosition, PlayerPositionConfig> = {
  [PlayerPosition.SOUTH]: {
    label: { x: CENTER_X, y: GAME_HEIGHT - 190 }, // Above the hand cards
    cards: { x: CENTER_X, y: HAND_Y, rotation: 0, spacing: 0, direction: 'h' },
    animationStart: { x: CENTER_X, y: HAND_Y },
    offScreen: { x: CENTER_X, y: GAME_HEIGHT + 100 },
    hakemReveal: { x: CENTER_X, y: GAME_HEIGHT - 150, rotation: 0 },
    turnToken: { x: -100, y: 0 }, // Left of label
  },
  [PlayerPosition.WEST]: {
    label: { x: LABEL_SIDE_MARGIN, y: CENTER_Y },
    cards: { x: SIDE_MARGIN, y: CENTER_Y - 50, rotation: Math.PI / 2, spacing: 15, direction: 'v' },
    animationStart: { x: SIDE_MARGIN, y: CENTER_Y },
    offScreen: { x: -100, y: CENTER_Y + 30 },
    hakemReveal: { x: 120, y: CENTER_Y, rotation: Math.PI / 2 },
    turnToken: { x: 0, y: -60 }, // Above label
  },
  [PlayerPosition.NORTH]: {
    label: { x: CENTER_X, y: 140 }, // Below the opponent cards
    cards: { x: CENTER_X, y: 50, rotation: 0, spacing: 20, direction: 'h' },
    animationStart: { x: CENTER_X, y: 60 },
    offScreen: { x: CENTER_X, y: -100 },
    hakemReveal: { x: CENTER_X, y: 180, rotation: 0 },
    turnToken: { x: 100, y: 0 }, // Right of label
  },
  [PlayerPosition.EAST]: {
    label: { x: GAME_WIDTH - LABEL_SIDE_MARGIN, y: CENTER_Y },
    cards: { x: GAME_WIDTH - SIDE_MARGIN, y: CENTER_Y - 50, rotation: -Math.PI / 2, spacing: 15, direction: 'v' },
    animationStart: { x: GAME_WIDTH - SIDE_MARGIN, y: CENTER_Y },
    offScreen: { x: GAME_WIDTH + 100, y: CENTER_Y + 30 },
    hakemReveal: { x: GAME_WIDTH - 120, y: CENTER_Y, rotation: -Math.PI / 2 },
    turnToken: { x: 0, y: -60 }, // Above label
  },
};

// Trick card offsets from center
export const TRICK_OFFSETS: Record<PlayerPosition, { x: number; y: number }> = {
  [PlayerPosition.SOUTH]: { x: 0, y: 60 },
  [PlayerPosition.WEST]: { x: -80, y: 0 },
  [PlayerPosition.NORTH]: { x: 0, y: -60 },
  [PlayerPosition.EAST]: { x: 80, y: 0 },
};

// Trick center position
export const TRICK_CENTER = { x: CENTER_X, y: CENTER_Y };

// Hand Y position (local player's cards)
export const PLAYER_HAND_Y = HAND_Y;

/**
 * 2-Player Layout: Players face each other top-bottom (vertical)
 * Local player at bottom center, opponent at top center
 * Nameplates positioned at corners outside the table ellipse
 */
export const PLAYER_POSITIONS_2P: Record<PlayerPosition, PlayerPositionConfig> = {
  [PlayerPosition.SOUTH]: {
    // Bottom center (local player)
    label: { x: GAME_WIDTH - 200, y: CENTER_Y + 150 }, // Bottom-right corner, outside table
    cards: { x: CENTER_X, y: HAND_Y, rotation: 0, spacing: 0, direction: 'h' },
    animationStart: { x: CENTER_X, y: HAND_Y },
    offScreen: { x: CENTER_X, y: GAME_HEIGHT + 100 },
    hakemReveal: { x: CENTER_X, y: GAME_HEIGHT - 150, rotation: 0 },
    turnToken: { x: -100, y: 0 }, // Left of label
  },
  [PlayerPosition.WEST]: {
    // Fallback - same as NORTH for 2P
    label: { x: 200, y: CENTER_Y - 150 }, // Top-left corner, outside table
    cards: { x: CENTER_X, y: 90, rotation: 0, spacing: 20, direction: 'h' },
    animationStart: { x: CENTER_X, y: 100 },
    offScreen: { x: CENTER_X, y: -100 },
    hakemReveal: { x: CENTER_X, y: 200, rotation: 0 },
    turnToken: { x: 100, y: 0 }, // Right of label
  },
  [PlayerPosition.NORTH]: {
    // Top center (opponent)
    label: { x: 200, y: CENTER_Y - 150 }, // Top-left corner, outside table
    cards: { x: CENTER_X, y: 90, rotation: 0, spacing: 20, direction: 'h' },
    animationStart: { x: CENTER_X, y: 100 },
    offScreen: { x: CENTER_X, y: -100 },
    hakemReveal: { x: CENTER_X, y: 200, rotation: 0 },
    turnToken: { x: 100, y: 0 }, // Right of label
  },
  [PlayerPosition.EAST]: {
    // Fallback - same as SOUTH for 2P
    label: { x: GAME_WIDTH - 200, y: CENTER_Y + 150 }, // Bottom-right corner, outside table
    cards: { x: CENTER_X, y: HAND_Y, rotation: 0, spacing: 0, direction: 'h' },
    animationStart: { x: CENTER_X, y: HAND_Y },
    offScreen: { x: CENTER_X, y: GAME_HEIGHT + 100 },
    hakemReveal: { x: CENTER_X, y: GAME_HEIGHT - 150, rotation: 0 },
    turnToken: { x: -100, y: 0 }, // Left of label
  },
};

/**
 * 3-Player Layout: Triangle formation
 * Local player at bottom center, both opponents at top (same height, spread apart)
 * Forms a V shape pointing down to the local player
 */
export const PLAYER_POSITIONS_3P: Record<PlayerPosition, PlayerPositionConfig> = {
  [PlayerPosition.SOUTH]: {
    // Bottom center (local player)
    label: { x: CENTER_X, y: GAME_HEIGHT - 190 },
    cards: { x: CENTER_X, y: HAND_Y, rotation: 0, spacing: 0, direction: 'h' },
    animationStart: { x: CENTER_X, y: HAND_Y },
    offScreen: { x: CENTER_X, y: GAME_HEIGHT + 100 },
    hakemReveal: { x: CENTER_X, y: GAME_HEIGHT - 150, rotation: 0 },
    turnToken: { x: -100, y: 0 }, // Left of label
  },
  [PlayerPosition.WEST]: {
    // Top-left opponent - cards tilted away (counterclockwise)
    label: { x: CENTER_X - 215, y: 185 },
    cards: { x: CENTER_X - 300, y: 140, rotation: -Math.PI / 6, spacing: 20, direction: 'h' },
    animationStart: { x: CENTER_X - 300, y: 150 },
    offScreen: { x: -100, y: -100 },
    hakemReveal: { x: CENTER_X - 215, y: 205, rotation: -Math.PI / 6 },
    turnToken: { x: -80, y: 0 }, // Left of label
  },
  [PlayerPosition.NORTH]: {
    // Top-right opponent - cards tilted away (clockwise)
    label: { x: CENTER_X + 215, y: 185 },
    cards: { x: CENTER_X + 300, y: 140, rotation: Math.PI / 6, spacing: 20, direction: 'h' },
    animationStart: { x: CENTER_X + 300, y: 150 },
    offScreen: { x: GAME_WIDTH + 100, y: -100 },
    hakemReveal: { x: CENTER_X + 215, y: 205, rotation: Math.PI / 6 },
    turnToken: { x: 80, y: 0 }, // Right of label
  },
  [PlayerPosition.EAST]: {
    // Unused in 3P, fallback to SOUTH positions
    label: { x: CENTER_X, y: GAME_HEIGHT - 190 },
    cards: { x: CENTER_X, y: HAND_Y, rotation: 0, spacing: 0, direction: 'h' },
    animationStart: { x: CENTER_X, y: HAND_Y },
    offScreen: { x: CENTER_X, y: GAME_HEIGHT + 100 },
    hakemReveal: { x: CENTER_X, y: GAME_HEIGHT - 150, rotation: 0 },
    turnToken: { x: -100, y: 0 }, // Fallback
  },
};

/**
 * Trick card offsets for 2P vertical layout
 * Cards meet in center from top and bottom
 */
export const TRICK_OFFSETS_2P: Record<PlayerPosition, { x: number; y: number }> = {
  [PlayerPosition.SOUTH]: { x: 0, y: 60 }, // Card moves up from bottom
  [PlayerPosition.WEST]: { x: 0, y: -60 }, // Fallback - same as NORTH
  [PlayerPosition.NORTH]: { x: 0, y: -60 }, // Card moves down from top
  [PlayerPosition.EAST]: { x: 0, y: 60 }, // Fallback - same as SOUTH
};

/**
 * Trick card offsets for 3P triangle layout
 * Cards meet in center from: bottom (SOUTH), top-left (WEST), top-right (NORTH)
 */
export const TRICK_OFFSETS_3P: Record<PlayerPosition, { x: number; y: number }> = {
  [PlayerPosition.SOUTH]: { x: 0, y: 60 }, // Bottom player, card goes up
  [PlayerPosition.WEST]: { x: -70, y: -50 }, // Top-left, card goes down-right
  [PlayerPosition.NORTH]: { x: 70, y: -50 }, // Top-right, card goes down-left
  [PlayerPosition.EAST]: { x: 0, y: 60 }, // Unused, fallback
};

/**
 * Get player positions based on player count.
 */
export function getPlayerPositions(playerCount: number): Record<PlayerPosition, PlayerPositionConfig> {
  switch (playerCount) {
    case 2:
      return PLAYER_POSITIONS_2P;
    case 3:
      return PLAYER_POSITIONS_3P;
    default:
      return PLAYER_POSITIONS;
  }
}

/**
 * Get trick offsets based on player count.
 */
export function getTrickOffsets(playerCount: number): Record<PlayerPosition, { x: number; y: number }> {
  switch (playerCount) {
    case 2:
      return TRICK_OFFSETS_2P;
    case 3:
      return TRICK_OFFSETS_3P;
    default:
      return TRICK_OFFSETS;
  }
}
